class CeyllaCartContent extends HTMLElement {
    static busy = false;
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      clearInterval(this.timer);
      const signal = this.abort.signal;
      this.root = window.Shopify?.routes?.root || '/';
      this.addEventListener('click', event => this.click(event), { signal });
      this.addEventListener('change', event => this.change(event), { signal });
      this.addEventListener('submit', event => this.submit(event), { signal });
      this.renderGoals();
      this.updateTimer();
      if (this.querySelector('[data-countdown]')) this.timer = setInterval(() => this.updateTimer(), 1000);
    }
    disconnectedCallback() { this.abort?.abort(); clearInterval(this.timer); }
    state() { return JSON.parse(this.querySelector('[data-cart-state]').textContent); }
    money(value) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:this.dataset.currency }).format(value/100); }
    status(message) { const target = document.querySelector('ceylla-cart-content [data-status]'); if (target) target.textContent = message; }
    renderGoals() {
      const goals = this.querySelector('[data-goals]');
      if (!goals) return;
      const steps = [...goals.querySelectorAll('[data-goal-step]')];
      const valid = steps.length && steps.every((step,i) => Number(step.dataset.amount)>0 && (!i || Number(step.dataset.amount)>Number(steps[i-1].dataset.amount)));
      if (!valid) { goals.hidden = true; return; }
      const totalFor = step => Number(step.dataset.total ?? this.dataset.goalTotal);
      const labels = JSON.parse(goals.querySelector('[data-goal-text]').textContent);
      const next = steps.find(step => totalFor(step) < Number(step.dataset.amount));
      const last = Number(steps[steps.length-1].dataset.amount);
      goals.querySelector('[data-goal-fill]').style.width = `${Math.min(100,totalFor(steps[steps.length-1])/last*100)}%`;
      goals.querySelector('[data-goal-message]').textContent = next ? labels.more.replace('__AMOUNT__',this.money(Number(next.dataset.amount)-totalFor(next))).replace('__REWARD__',next.dataset.label) : labels.done;
      steps.forEach(step => { const done=totalFor(step)>=Number(step.dataset.amount); step.toggleAttribute('data-unlocked',done); step.setAttribute('aria-label',done ? labels.unlocked.replace('__REWARD__',step.dataset.label):step.dataset.label); });
    }
    updateTimer() {
      const timer = this.querySelector('[data-countdown]');
      if (!timer) return;
      const remaining = Date.parse(timer.dataset.end)-Date.now();
      timer.hidden = !Number.isFinite(remaining) || remaining <= 0;
      if (timer.hidden) return;
      const seconds=Math.floor(remaining/1000), days=Math.floor(seconds/86400), hours=Math.floor(seconds%86400/3600), minutes=Math.floor(seconds%3600/60);
      timer.querySelector('[data-time]').textContent = `${days ? days+'d ' : ''}${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}:${String(seconds%60).padStart(2,'0')}`;
    }
    async request(path, payload) {
      const options = payload ? { method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(payload) } : {headers:{Accept:'application/json'}};
      const response = await fetch(this.root+path,options);
      const result=await response.json();
      if (!response.ok || result.status || result.errors) throw new Error(typeof result.description==='string'?result.description:typeof result.errors==='string'?result.errors:this.dataset.error);
      return result;
    }
    sectionData() { return {sections:['cart-drawer','cart-icon-bubble'],sections_url:location.pathname}; }
    async refresh(result) {
      const cart = result?.item_count !== undefined ? result : await this.request('cart.js');
      let sections = result?.sections;
      if (!sections?.['cart-drawer'] || !sections?.['cart-icon-bubble']) {
        const url = new URL(location.href);
        url.searchParams.set('sections','cart-drawer,cart-icon-bubble');
        const response = await fetch(url);
        if (!response.ok) throw new Error(this.dataset.error);
        sections = await response.json();
      }
      const drawer = document.querySelector('cart-drawer');
      const body = this.querySelector('.cc__body');
      const scrollTop = body?.scrollTop || 0;
      const active = document.activeElement;
      const activeRow = active?.closest('[data-line]');
      const activeKey = activeRow?.dataset.key;
      const focusAttribute = ['data-quantity-input','data-line-variant','data-quantity-change'].find(a=>active?.hasAttribute(a));
      const focusValue = focusAttribute ? active.getAttribute(focusAttribute) : '';
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
      const incoming = parsed.querySelector('#CartDrawer');
      const target = drawer?.querySelector('#CartDrawer');
      if (!incoming || !target) throw new Error(this.dataset.error);
      target.innerHTML = incoming.innerHTML;
      drawer.classList.toggle('is-empty',cart.item_count === 0);
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'] || '', 'text/html');
      const bubbleContent = bubbleDoc.querySelector('.shopify-section');
      if (bubble && bubbleContent) bubble.innerHTML = bubbleContent.innerHTML;
      const updated = target.querySelector('ceylla-cart-content');
      const newBody = updated?.querySelector('.cc__body');
      if (newBody) newBody.scrollTop = scrollTop;
      if (drawer.classList.contains('active')) {
        const rows = [...updated.querySelectorAll('[data-line]')];
        const row = rows.find(r=>r.dataset.key===activeKey);
        const next = focusAttribute && row ? [...row.querySelectorAll(`[${focusAttribute}]`)].find(e=>e.getAttribute(focusAttribute)===focusValue) : null;
        trapFocus(drawer.querySelector('.drawer__inner'),next || updated.querySelector('[data-close-cart]'));
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate,{source:'ceylla-cart',cartData:cart});
      this.status(this.dataset.updated);
      return cart;
    }
    async operate(task, focus) {
      if (CeyllaCartContent.busy) return;
      CeyllaCartContent.busy=true;
      this.setAttribute('aria-busy','true');
      this.status('');
      const disabled=[...this.querySelectorAll('button:not([data-close-cart]),input,select,textarea')].filter(el=>!el.disabled);
      disabled.forEach(el=>el.disabled=true);
      try { const result=await task(); await this.refresh(result); }
      catch(error) { try { await this.refresh(); } catch {} this.status(error.message || this.dataset.error); }
      finally {
        CeyllaCartContent.busy=false;
        this.removeAttribute('aria-busy');
        disabled.forEach(el=>{if(el.isConnected)el.disabled=false;});
        if (focus) setTimeout(()=>document.querySelector(focus)?.focus({preventScroll:true}),50);
      }
    }
    click(event) {
      const button=event.target.closest('button');
      if (!button || !this.contains(button)) return;
      const row=button.closest('[data-line]');
      if (button.hasAttribute('data-close-cart')) { event.preventDefault(); this.closest('cart-drawer')?.close(); }
      else if (button.hasAttribute('data-remove')) { event.preventDefault(); this.operate(()=>this.request('cart/change.js',{id:row.dataset.key,quantity:0,...this.sectionData()})); }
      else if (button.hasAttribute('data-quantity-change')) {
        event.preventDefault(); const input=row.querySelector('[data-quantity-input]');
        const quantity=Number(input.value)+Number(button.dataset.quantityChange)*Number(input.step || 1);
        if(quantity < Number(input.min||1) || input.max && quantity>Number(input.max)) return;
        this.operate(()=>this.request('cart/change.js',{id:row.dataset.key,quantity,...this.sectionData()}));
      } else if(button.hasAttribute('data-add-suggestion')) {
        event.preventDefault(); const select=button.closest('[data-suggestion]').querySelector('[data-suggestion-variant]');
        this.operate(()=>this.request('cart/add.js',{items:[{id:Number(select.value),quantity:1}],...this.sectionData()}));
      } else if(button.hasAttribute('data-protection')) {
        event.preventDefault(); this.operate(()=>button.dataset.key ? this.request('cart/change.js',{id:button.dataset.key,quantity:0,...this.sectionData()}) : this.request('cart/add.js',{items:[{id:Number(button.dataset.id),quantity:1,properties:{_ceylla_protection:'true'}}],...this.sectionData()}));
      } else if(button.hasAttribute('data-slide')) {
        const track=this.querySelector('[data-suggestions]');
        track.scrollBy({left:Number(button.dataset.slide)*(track.clientWidth+10),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      }
    }
    change(event) {
      const field=event.target, row=field.closest('[data-line]');
      if(field.hasAttribute('data-quantity-input')) {
        if(!field.checkValidity()) {field.reportValidity();field.value=row.dataset.quantity;return;}
        this.operate(()=>this.request('cart/change.js',{id:row.dataset.key,quantity:Number(field.value),...this.sectionData()}));
      } else if(field.hasAttribute('data-line-variant')) {
        const id=Number(field.value);
        if(id===Number(row.dataset.variant)) return;
        this.operate(()=>this.swapVariant(row,id));
      } else if(field.hasAttribute('data-suggestion-variant')) field.closest('[data-suggestion]').querySelector('[data-suggestion-price]').textContent=field.selectedOptions[0].dataset.price;
    }
    sameProperties(a,b) { const normalize=x=>JSON.stringify(Object.entries(x||{}).sort(([a],[b])=>a.localeCompare(b))); return normalize(a)===normalize(b); }
    async swapVariant(row,id) {
      const before=await this.request('cart.js');
      const original=before.items.find(item=>item.key===row.dataset.key);
      if(!original) throw new Error(this.dataset.review);
      const existing=before.items.find(item=>item.variant_id===id && !item.selling_plan_allocation && this.sameProperties(item.properties,original.properties));
      let added=false;
      try {
        await this.request('cart/add.js',{items:[{id,quantity:original.quantity,properties:original.properties||{}}]});
        added=true;
        const current=await this.request('cart.js');
        const old=current.items.find(item=>item.key===original.key) || current.items.find(item=>item.variant_id===original.variant_id && this.sameProperties(item.properties,original.properties));
        if(!old) throw new Error(this.dataset.review);
        return await this.request('cart/change.js',{id:old.key,quantity:0,...this.sectionData()});
      } catch(error) {
        if(added) {
          try {
            const current=await this.request('cart.js');
            const line=current.items.find(item=>item.variant_id===id && !item.selling_plan_allocation && this.sameProperties(item.properties,original.properties));
            if(line) await this.request('cart/change.js',{id:line.key,quantity:existing?.quantity||0});
          } catch {throw new Error(this.dataset.review);}
        }
        throw error;
      }
    }
    submit(event) {
      const form=event.target;
      if(form.matches('[data-note-form]')) {event.preventDefault(); const note=form.elements.note.value; this.operate(()=>this.request('cart/update.js',{note,...this.sectionData()}));}
      else if(form.matches('[data-discount-form]')) {
        event.preventDefault(); const code=form.elements.discount.value.trim(); if(!code) return;
        this.operate(async()=>{
          const cart=await this.request('cart.js');
          const existing=(cart.discount_codes||[]).filter(x=>x.applicable).map(x=>x.code);
          const result=await this.request('cart/update.js',{discount:[...new Set([...existing,code])].join(','),...this.sectionData()});
          const applied=result.discount_codes?.find(x=>x.code.toLowerCase()===code.toLowerCase());
          if(!applied?.applicable) throw new Error(this.dataset.discountError || this.dataset.error);
          return result;
        });
      } else if(CeyllaCartContent.busy) event.preventDefault();
    }
  }
  if(!customElements.get('ceylla-cart-content')) customElements.define('ceylla-cart-content',CeyllaCartContent);

document.addEventListener('submit', event => {
  if (CeyllaCartContent.busy && (event.target.matches('form[action*="/cart"]') || event.submitter?.name === 'checkout')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
